import type { IssueDetail } from '@cortex/domain-ac-mens-hub-api';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatIssueTime, ISSUE_VOICE_SOURCE_LABEL_KEYS } from '../../lib/issue-mgmt-format.js';
import { IssueText } from './IssueText.js';

/**
 * 一度に出す声の件数。
 * @graph-connects none
 */
export const ISSUE_VOICE_PAGE_SIZE = 20;

/**
 * 課題に紐づく声（新しい順）。見出しに全期間と直近30日の件数を出し、20件ずつ出す。
 * 非公開チャンネル由来の声には札を付け、Slack 由来は「Slackで開く」、旧表由来は旧番号、手で入れた声は発生元を出す。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題に紐づく声を新しい順に見せ、原文の場所（Slack の投稿・旧表の番号・発生元）へ1クリックで行けるようにする
 * @graph-connects IssueText [calls] 伏せ字の札
 * @graph-connects formatIssueTime [calls] 投稿日時
 */
export function IssueVoices({ issue, now }: { issue: IssueDetail; now: Date }) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(ISSUE_VOICE_PAGE_SIZE);
  const rest = issue.voices.length - shown;
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="flex items-baseline gap-3 border-b border-[var(--color-border)] px-4 py-3">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {issue.voiceCount === 0
            ? t('issueMgmt.voices.empty')
            : t('issueMgmt.voices.heading', { n: String(issue.voiceCount) })}
        </h2>
        {issue.voiceCount > 0 && (
          <span className="text-xs text-[var(--color-text-muted)]">
            {t('issueMgmt.voices.sub', { n: String(issue.recentVoiceCount) })}
          </span>
        )}
      </div>
      <ul className="divide-y divide-[var(--color-border)]">
        {issue.voices.slice(0, shown).map((voice) => (
          <li
            key={voice.pointId}
            className="flex flex-wrap items-start gap-x-4 gap-y-1 px-4 py-2.5 text-sm"
          >
            <span className="w-40 shrink-0 text-xs text-[var(--color-text-muted)]">
              {formatIssueTime(voice.postedAt, voice.postedDate, now) ?? t('issueMgmt.common.dash')}{' '}
              {voice.channelName === null ? '' : `#${voice.channelName}`}
              {voice.isPrivate && (
                <span className="ml-1" title={t('issueMgmt.common.privateHint')}>
                  <Badge size="sm">{t('issueMgmt.common.private')}</Badge>
                </span>
              )}
            </span>
            <span className="min-w-0 flex-1 text-[var(--color-text-primary)]">
              {voice.bodyDeleted ? (
                <span className="text-[var(--color-text-muted)]">
                  {t('issueMgmt.voices.deleted')}
                </span>
              ) : (
                <IssueText text={voice.pointTitle} />
              )}
              {voice.repliedAt !== null && (
                <span className="ml-2 text-xs text-[var(--color-success)]">
                  {t('issueMgmt.voices.replied')}
                </span>
              )}
            </span>
            <span className="shrink-0 text-xs">
              {voice.slackUrl !== null ? (
                <a
                  href={voice.slackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[var(--color-accent-primary)] hover:underline"
                >
                  {t('issueMgmt.common.openSlack')}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
              ) : voice.manualSource !== null ? (
                <span className="text-[var(--color-text-muted)]">
                  {t(ISSUE_VOICE_SOURCE_LABEL_KEYS[voice.manualSource])}
                </span>
              ) : voice.legacyIds.length > 0 ? (
                <span className="text-[var(--color-text-muted)]">
                  {t('issueMgmt.voices.legacy', { ids: voice.legacyIds.join('・') })}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {rest > 0 && (
        <button
          type="button"
          onClick={() => {
            setShown((n) => n + ISSUE_VOICE_PAGE_SIZE);
          }}
          className="w-full border-t border-[var(--color-border)] px-4 py-2.5 text-left text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)]"
        >
          {t('issueMgmt.voices.older', { n: String(rest) })}
        </button>
      )}
    </section>
  );
}
